<!-- src/components/SessionTimeoutModal.vue - Updated with Better UX -->
<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
  >
    <!-- Background overlay -->
    <div
      class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
    ></div>

    <!-- Modal content -->
    <div
      class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
      >
        <!-- Header -->
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <!-- Warning icon -->
            <div
              class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10"
            >
              <Clock class="h-6 w-6 text-yellow-600" />
            </div>

            <!-- Content -->
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3
                id="modal-title"
                class="text-lg leading-6 font-medium text-gray-900"
              >
                Session Expiring Soon
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500">
                  Your session will expire in
                  <span class="font-semibold text-red-600">{{
                    formattedTimeRemaining
                  }}</span
                  >. Would you like to extend your session?
                </p>
                <p class="text-xs text-gray-400 mt-2">
                  Click "Extend Session" to continue working or "Log Out" to end
                  your session now.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action buttons -->
        <div class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
          <button
            @click="handleExtend"
            :disabled="extending"
            type="button"
            class="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto transition-all duration-200"
          >
            <span v-if="!extending" class="flex items-center">
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              Extend Session
            </span>
            <span v-else class="flex items-center">
              <svg
                class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Extending...
            </span>
          </button>

          <button
            @click="handleLogout"
            :disabled="extending"
            type="button"
            class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto transition-all duration-200"
          >
            <span class="flex items-center">
              <svg
                class="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                ></path>
              </svg>
              Log Out
            </span>
          </button>
        </div>

        <!-- Error message display -->
        <div v-if="error" class="bg-red-50 border-t border-red-200 px-4 py-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-red-800">{{ error }}</p>
              <button
                @click="clearError"
                class="mt-1 text-xs text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>

        <!-- Success message display -->
        <div
          v-if="success"
          class="bg-green-50 border-t border-green-200 px-4 py-3"
        >
          <div class="flex">
            <div class="flex-shrink-0">
              <svg
                class="h-5 w-5 text-green-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.236 4.53L7.53 10.5a.75.75 0 00-1.06 1.06l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-green-800">{{ success }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Clock } from 'lucide-vue-next';
import { useAuthStore } from '@/stores/auth';

// Props
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  timeRemaining: {
    type: Number,
    required: true,
  },
});

// Emits
const emit = defineEmits(['extend', 'logout', 'close']);

// Store
const authStore = useAuthStore();

// State
const extending = ref(false);
const error = ref(null);
const success = ref(null);
const countdownTimer = ref(null);

// Computed
const formattedTimeRemaining = computed(() => {
  const totalSeconds = Math.ceil(props.timeRemaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (totalSeconds <= 0) {
    return '0s';
  }

  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
});

// Methods
const handleExtend = async () => {
  extending.value = true;
  error.value = null;
  success.value = null;

  try {
    console.log('🔄 Modal triggering session extension...');

    const result = await authStore.extendSession();

    if (result) {
      success.value = 'Session extended successfully!';
      console.log('✅ Session extended via modal');

      // Close modal after short delay to show success message
      setTimeout(() => {
        emit('extend');
        emit('close');
      }, 1500);
    } else {
      throw new Error('Failed to extend session');
    }
  } catch (err) {
    console.error('❌ Modal session extension error:', err);
    error.value =
      err.message || 'Failed to extend session. Please try logging in again.';
  } finally {
    extending.value = false;
  }
};

const handleLogout = () => {
  console.log('🚪 User chose to logout from session modal');
  emit('logout');
};

const clearError = () => {
  error.value = null;
};

// Auto-logout when time expires
const checkTimeExpired = () => {
  if (props.timeRemaining <= 0) {
    console.log('⏰ Time expired in session modal, auto-logging out');
    handleLogout();
  }
};

// Lifecycle
onMounted(() => {
  // Start countdown timer
  countdownTimer.value = setInterval(checkTimeExpired, 1000);

  // Focus on extend button for better UX
  setTimeout(() => {
    const extendButton = document.querySelector('[data-extend-button]');
    if (extendButton) {
      extendButton.focus();
    }
  }, 100);
});

onUnmounted(() => {
  if (countdownTimer.value) {
    clearInterval(countdownTimer.value);
  }
});

// Keyboard event handling
const handleKeyDown = event => {
  if (event.key === 'Escape' && !extending.value) {
    handleLogout();
  } else if (event.key === 'Enter' && !extending.value) {
    handleExtend();
  }
};

// Add keyboard listener when modal is shown
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleKeyDown);

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });
}
</script>

<style scoped>
/* Modal animations */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Ensure modal appears above everything */
.fixed {
  z-index: 9999;
}

/* Button hover effects */
button:hover:not(:disabled) {
  transform: translateY(-1px);
}

button:active:not(:disabled) {
  transform: translateY(0);
}

/* Focus styles for accessibility */
button:focus-visible {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}
</style>
